import Sentry from '../errors/sentry';

/**
 * The app never renders <font>. Chrome's and Edge's page translators wrap every text node they
 * translate in a pair of them, so finding one means the page has been translated.
 */
const isPageTranslated = () => document.querySelector('font') !== null;

/**
 * EN-956: keep browser page translation from corrupting Vue's view of the DOM.
 *
 * Chrome and Edge translate a page by swapping each text node for <font> wrappers, which detaches
 * the node Vue still holds as a vnode's `el`. The next patch that anchors on it - a v-if shown
 * beside translated text, say - throws NotFoundError from insertBefore and aborts halfway, leaving
 * vnodes with no component instance. Every later render or unmount of that subtree then crashes on
 * null (`emitsOptions`, `bum`, `subTree`, `parentNode`), which takes navigation down with it.
 *
 * When the reference node is no longer a child, append instead of throwing. Content shown next to
 * translated text can land at the end of its parent rather than in place, which is the price of
 * keeping the page working. Inside a fragment Vue diffs in full (a render function, or reka-ui's
 * asChild with several children) that lands it past the fragment's end anchor, so it can outlive
 * the fragment's removal and show twice if the fragment renders it again. Well-formed calls are
 * untouched. removeChild needs no guard: Vue reads `parentNode` before removing, so it never asks a
 * parent to remove a detached node.
 *
 * Off a translated page nothing should move our nodes, so a mismatch there is reported, with the
 * call site's stack, rather than silently absorbed.
 *
 * Call once per entry, before the Vue app mounts.
 *
 * @returns {() => void} Teardown that restores the native method.
 */
export const installTranslationGuard = () => {
	const { insertBefore } = Node.prototype;

	Node.prototype.insertBefore = function (node, reference) {
		if (reference && reference.parentNode !== this) {
			if (!isPageTranslated()) {
				Sentry.captureException(new Error('dom mismatch tolerated'), {
					level: 'warning',
					tags: { parent: this.nodeName, node: node.nodeName, reference: reference.nodeName },
				});
			}

			return insertBefore.call(this, node, null);
		}

		return insertBefore.call(this, node, reference);
	};

	return () => {
		Node.prototype.insertBefore = insertBefore;
	};
};
